/**
 * Phase 10e — Historique des éditions sur une facture fournisseur (audit log).
 *
 * Affiché uniquement si `invoice.audit_log` contient au moins une entrée.
 * Chaque entrée : { at, user_id, user_email, user_name, changes: { field: { old, new } } }
 *
 * Interne : aucune donnée de marge n'est jamais mentionnée ici (les champs éditables sont
 * limités par le backend à supplier_*, invoice_number, dates, amounts HTVA/VAT/TVAC, refs, notes).
 */
import { History } from "lucide-react";

const FIELD_LABELS = {
  supplier_name: "Fournisseur",
  supplier_vat: "N° TVA fournisseur",
  supplier_iban: "IBAN",
  invoice_number: "N° facture",
  issue_date: "Date facture",
  due_date: "Échéance",
  amount_htva: "Montant HTVA",
  amount_vat: "TVA",
  amount_tvac: "TVAC",
  currency: "Devise",
  purchase_order_ref: "Bon de commande",
  payment_reference: "Communication paiement",
  internal_notes: "Notes internes",
};

function shortDateTimeBE(iso) {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  const date = dt.toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
  const h = String(dt.getHours()).padStart(2, "0");
  const m = String(dt.getMinutes()).padStart(2, "0");
  return `${date} · ${h}h${m}`;
}

function formatValue(v) {
  if (v === null || v === undefined || v === "") return "∅";
  if (typeof v === "number") return String(v);
  return String(v);
}

export default function SupplierInvoiceAuditHistory({ invoice }) {
  const log = Array.isArray(invoice?.audit_log) ? invoice.audit_log : [];
  if (log.length === 0) return null;

  // Du plus récent au plus ancien
  const sorted = [...log].sort((a, b) => {
    const ta = new Date(a?.at || 0).getTime();
    const tb = new Date(b?.at || 0).getTime();
    return tb - ta;
  });

  return (
    <div
      className="rounded border border-slate-800 bg-slate-950/40 p-3"
      data-testid="supplier-invoice-audit-history"
    >
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wide text-slate-500">
          <History className="h-3.5 w-3.5" />
          Historique éditions ({sorted.length})
        </div>
      </div>
      <ol className="space-y-3">
        {sorted.map((entry, i) => {
          const who = entry?.user_name || entry?.user_email || "?";
          const changes = entry?.changes || {};
          const fields = Object.keys(changes);
          return (
            <li
              key={`${entry?.at || "e"}-${i}`}
              className="rounded border border-slate-800/60 bg-slate-900/40 p-2"
              data-testid={`audit-entry-${i}`}
            >
              <div className="mb-1 flex items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-slate-200">{who}</span>
                <span className="text-xs text-slate-500">{shortDateTimeBE(entry?.at)}</span>
              </div>
              <ul className="space-y-0.5 text-xs">
                {fields.map((f) => {
                  const c = changes[f] || {};
                  return (
                    <li key={f} className="flex flex-wrap items-baseline gap-1 text-slate-400">
                      <span className="text-slate-500">{FIELD_LABELS[f] || f} :</span>
                      <span className="line-through text-slate-500">{formatValue(c.old)}</span>
                      <span className="text-slate-500">→</span>
                      <span className="font-medium text-slate-200">{formatValue(c.new)}</span>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
